import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '../../contexts/AuthContext';
import { isPlatformAdminRole } from '../../config/constants';
import Layout from '../Layout/Layout';
import Login from '../Auth/Login';
import ResetPassword from '../Auth/ResetPassword';
import Dashboard from '../Dashboard/Dashboard';
import Enquiries from '../Enquiries/Enquiries';
import Blog from '../Blog/Blog';
import BusinessSettings from '../Settings/BusinessSettings';
import ChangeRequests from '../ChangeRequests/ChangeRequests';
import Profile from '../Profile/Profile';
import Support from '../Support/Support';
import HelpVideos from '../HelpVideos/HelpVideos';
import AdminTenants from '../Admin/AdminTenants';
import AdminUsers from '../Admin/AdminUsers';
import AdminChangeRequests from '../Admin/AdminChangeRequests';

const isAdminRole = isPlatformAdminRole;

// Redirect component for PocketBase default reset links
// Handles format: /#/auth/confirm-password-reset/:token
const ConfirmPasswordResetRedirect = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      // Redirect to our clean reset-password route with query param
      navigate(`/reset-password?token=${encodeURIComponent(token)}`, { replace: true });
    } else {
      // Fallback to login if no token
      navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 font-medium">Redirecting to password reset...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, currentUserRole, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !isAdminRole(currentUserRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route Component (redirects if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, currentUserRole, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect based on role
    if (isAdminRole(currentUserRole)) {
      return <Navigate to="/admin/tenants" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/admin-login" element={<PublicRoute><Login isAdminLogin={true} /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      
      {/* PocketBase Default Link Redirect Route */}
      <Route path="/auth/confirm-password-reset/:token" element={<ConfirmPasswordResetRedirect />} />

      {/* Protected Client Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/enquiries" element={<ProtectedRoute><Layout><Enquiries /></Layout></ProtectedRoute>} />
      <Route path="/blog" element={<ProtectedRoute><Layout><Blog /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><BusinessSettings /></Layout></ProtectedRoute>} />
      <Route path="/change-requests" element={<ProtectedRoute><Layout><ChangeRequests /></Layout></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Layout><HelpVideos /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Layout><Support /></Layout></ProtectedRoute>} />

      {/* Protected Admin Routes */}
      <Route path="/admin/tenants" element={<ProtectedRoute requireAdmin={true}><Layout><AdminTenants /></Layout></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin={true}><Layout><AdminUsers /></Layout></ProtectedRoute>} />
      <Route path="/admin/requests" element={<ProtectedRoute requireAdmin={true}><Layout><AdminChangeRequests /></Layout></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

const Router = () => {
  return (
    <HashRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </HashRouter>
  );
};

export default Router;